<template>
  <BaseModal
    title="Transaction timeline"
    @cancel="emit('close')"
  >
    <div class="w-full max-h-[70vh] overflow-y-auto pr-1">
      <div v-if="reference && reference.raw" class="mb-4 rounded-md bg-gray-50 p-4">
        <div class="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
          <div>
            <p class="text-xs text-gray-500">Widget</p>
            <p class="text-sm font-medium text-gray-900 truncate" :title="reference.widgetId">{{ reference.widgetId || '-' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">Principal</p>
            <p class="text-sm font-medium text-gray-900">{{ reference.principalId || '-' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">Reservation</p>
            <p class="text-sm font-medium text-gray-900">{{ reference.reservationId || '-' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">Payment record</p>
            <p class="text-sm font-medium text-gray-900">{{ reference.paymentGatewayId || '-' }}</p>
          </div>
        </div>
        <div class="mt-3 flex items-center gap-1.5 border-t border-gray-200 pt-2">
          <p class="text-xs text-gray-500 truncate" :title="reference.raw">{{ reference.raw }}</p>
          <button
            type="button"
            class="shrink-0 text-gray-400 hover:text-primary-main transition-colors"
            :title="copied === 'reference' ? 'Copied!' : 'Copy booking reference'"
            @click="copy(reference.raw, 'reference')"
          >
            <CopyCheckIcon :copied="copied === 'reference'" />
          </button>
        </div>
      </div>

      <ol v-if="rows.length" class="relative ml-2 border-l border-gray-200">
        <li v-for="row in rows" :key="row.id" class="mb-6 ml-5 last:mb-1">
          <span
            class="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full"
            :class="dotColor(row.statusCode)"
          />
          <div class="flex flex-wrap items-center gap-2">
            <time class="text-xs text-gray-500">{{ row.transactionTimestamp }}</time>
            <span class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium" :class="typeBadge(row.type).classes">
              {{ typeBadge(row.type).label }}
            </span>
            <span class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium" :class="statusBadge(row.statusCode).classes">
              {{ statusBadge(row.statusCode).label }}
            </span>
            <span v-if="row.source" class="text-xs text-gray-400">via {{ row.source }}</span>
            <span v-if="row.amount" class="ml-auto text-xs font-medium text-gray-600">{{ row.amount }}</span>
          </div>
          <p v-if="row.transactionStatus" class="mt-1 text-sm text-gray-700">{{ row.transactionStatus }}</p>
          <p v-if="row.transactionReferenceId" class="mt-0.5 text-xs text-gray-400 break-all">{{ row.transactionReferenceId }}</p>
          <div v-if="row.responsePayload" class="mt-2">
            <button
              type="button"
              class="text-xs text-action-primary hover:text-primary-dark transition-colors"
              @click="toggled[row.id] = !toggled[row.id]"
            >
              {{ toggled[row.id] ? 'Hide payload' : 'Show payload' }}
            </button>
            <div v-if="toggled[row.id]" class="relative mt-1 rounded-md bg-gray-100 p-3">
              <button
                type="button"
                class="absolute right-2 top-2 text-gray-400 hover:text-primary-main transition-colors"
                :title="copied === `payload-${row.id}` ? 'Copied!' : 'Copy payload'"
                @click="copy(prettyPayload(row.responsePayload), `payload-${row.id}`)"
              >
                <CopyCheckIcon :copied="copied === `payload-${row.id}`" />
              </button>
              <pre class="overflow-x-auto text-xs text-gray-700">{{ prettyPayload(row.responsePayload) }}</pre>
            </div>
          </div>
        </li>
      </ol>
      <p v-else class="py-6 text-center text-sm text-secondary">No rows found for this transaction.</p>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue';

import BaseModal from '@/components/common/BaseModal.vue';
import CopyCheckIcon from './CopyCheckIcon.vue';

import { PaymentGatewayLogsI, TransactionReferenceI } from '@/interfaces/interfaces';
import { statusBadge, typeBadge } from './logBadges';
import { copyText } from './clipboard';

defineProps<{
  reference: TransactionReferenceI | null;
  rows: PaymentGatewayLogsI[];
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const toggled = reactive<Record<number, boolean>>({});
const copied = ref<string | null>(null);

const dotColor = (statusCode: string): string => {
  switch (statusCode) {
    case 'success':
      return 'bg-green-400';
    case 'failed':
    case 'error':
      return 'bg-red-400';
    case 'cancelled':
      return 'bg-orange-400';
    default:
      return 'bg-gray-300';
  }
};

const prettyPayload = (payload: string): string => {
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
};

const copy = (text: string, key: string) => {
  copyText(text).then(() => {
    copied.value = key;
    setTimeout(() => {
      if (copied.value === key) {
        copied.value = null;
      }
    }, 1500);
  });
};
</script>
